import * as React from "react";
import { Select, theme, Tooltip } from "antd";
import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import { Flex } from "src/components/Flex";

interface TeamData {
  id: string;
  name: string;
  description?: string;
  users: string[];
}

interface TeamSelectorProps {
  value?: string | string[];
  onChange: (teamIds: string | string[]) => void;
  teams?: TeamData[];
  loading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  mode?: "multiple" | undefined;
  defaultOpen?: boolean;
}

export function TeamSelector({
  value,
  onChange,
  teams = [],
  loading = false,
  placeholder = "Select team...",
  disabled = false,
  mode,
  defaultOpen = false,
}: TeamSelectorProps) {
  const { token } = theme.useToken();

  // Enhanced search function that handles partial and out-of-order matches
  const searchTeam = React.useCallback((input: string, option: any) => {
    if (!input.trim()) return true;
    const searchTerms = input.toLowerCase().split(/\s+/);
    return searchTerms.every((term) =>
      option.searchText.toLowerCase().includes(term)
    );
  }, []);

  const options = React.useMemo(() => {
    return teams.map((team) => ({
      value: team.id,
      searchText: `${team.name} ${team.description || ""}`,
      label: (
        <Tooltip title={team.description} placement="right">
          <Flex gap={8} style={{ alignItems: "center", padding: "4px 0" }}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  color: token.colorText,
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                }}
              >
                {team.name}
              </div>
              {team.users?.length > 0 && (
                <div
                  style={{
                    color: token.colorTextTertiary,
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <UserOutlined style={{ fontSize: "12px" }} />
                  {team.users.length} members
                </div>
              )}
            </div>
          </Flex>
        </Tooltip>
      ),
    }));
  }, [teams, token]);

  return (
    <Select
      showSearch
      mode={mode}
      value={value}
      onChange={onChange}
      loading={loading}
      disabled={disabled}
      options={options}
      defaultOpen={defaultOpen}
      placeholder={placeholder}
      filterOption={searchTeam}
      optionFilterProp="searchText"
      style={{
        minWidth: 240,
        backgroundColor: token.colorBgContainer,
      }}
      className="enhanced-team-selector"
      dropdownStyle={{
        padding: "4px",
      }}
    ></Select>
  );
}
